import { useState, useEffect, useRef, useCallback } from "react";

// ─── Game Constants ───────────────────────────────────────────────────────────
const FOODS   = ["🍪", "🎂", "🍡", "🍮", "🍯", "🌾"];
const FNAMES  = ["කොකිස්", "කාවුම්", "අග්ගළා", "කිරිබත්", "හල්පේ", "රොටී"];
const FCOLORS = ["#d4820a", "#b83232", "#3d8a28", "#c9a800", "#b04a10", "#8a7030"];
const FGLOW   = ["#ffcc44", "#ff6666", "#88ee44", "#ffee44", "#ff9944", "#ddcc66"];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const fc    = i => FCOLORS[i % FCOLORS.length];
const fg    = i => FGLOW[i % FGLOW.length];
const fe    = i => FOODS[i % FOODS.length];
const rnd   = n => Math.floor(Math.random() * n);

// ─── Pure helpers ─────────────────────────────────────────────────────────────
function makeBubble(col, row, food, R) {
  const xOff = row % 2 === 0 ? 0 : R;
  return {
    col, row, food,
    x: xOff + R + col * R * 2,
    y: R * 0.5 + row * R * 1.72,
    r: R - 2,
    scale: 1, alpha: 1,
    state: "idle",   // idle | marked | popping | floating-fall
    popT: 0, markT: 0,
  };
}

function getBubble(arr, col, row) {
  return arr.find(
    b => b.col === col && b.row === row &&
         b.state !== "popping" && b.state !== "floating-fall"
  );
}

function neighbors(b, arr) {
  const dirs = b.row % 2 === 0
    ? [[1,0],[-1,0],[0,-1],[1,-1],[0,1],[1,1]]
    : [[1,0],[-1,0],[0,-1],[-1,-1],[0,1],[-1,1]];
  return dirs.map(([dc,dr]) => getBubble(arr, b.col+dc, b.row+dr)).filter(Boolean);
}

function flood(start, arr, test) {
  const seen = new Set([start]);
  const q    = [start];
  while (q.length) {
    const cur = q.shift();
    neighbors(cur, arr).forEach(n => {
      if (!seen.has(n) && test(n)) { seen.add(n); q.push(n); }
    });
  }
  return [...seen];
}

function matchGroup(b, arr)   { return flood(b, arr, n => n.food === b.food); }

function floatingSet(arr) {
  const anchored = new Set();
  arr.filter(b => b.row === 0 && b.state === "idle")
    .forEach(b => flood(b, arr, () => true).forEach(n => anchored.add(n)));
  return arr.filter(b => !anchored.has(b) && b.state === "idle");
}

function buildGrid(level, R, COLS) {
  const rows = Math.min(4 + level, 9);
  const out  = [];
  for (let row = 0; row < rows; row++) {
    const cols = row % 2 === 0 ? COLS : COLS - 1;
    for (let col = 0; col < cols; col++) {
      out.push(makeBubble(col, row, rnd(Math.min(3 + Math.floor(level / 2), 6)), R));
    }
  }
  return out;
}

function getDims() {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const R = Math.floor(Math.min(W / 12, H / 22, 30));
  const COLS = Math.floor(W / (R * 2));
  return { W, H, R, COLS };
}

// ─── Draw helpers ─────────────────────────────────────────────────────────────
function drawBubble(ctx, food, x, y, r, alpha, glowPulse, marked) {
  if (r <= 0 || alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha ?? 1;

  if (marked || (glowPulse > 0)) {
    const gSz = r * (1.5 + (glowPulse || 0) * 0.6);
    const g = ctx.createRadialGradient(x, y, 0, x, y, gSz);
    g.addColorStop(0, fg(food) + "99");
    g.addColorStop(1, fg(food) + "00");
    ctx.beginPath();
    ctx.arc(x, y, gSz, 0, Math.PI * 2);
    ctx.fillStyle = g;
    ctx.fill();
  }

  ctx.shadowColor = fc(food) + "aa";
  ctx.shadowBlur  = marked ? 16 : 7;

  const grad = ctx.createRadialGradient(x - r * 0.32, y - r * 0.32, r * 0.08, x, y, r);
  grad.addColorStop(0,    "rgba(255,255,255,0.78)");
  grad.addColorStop(0.35,  fc(food) + "ee");
  grad.addColorStop(0.75,  fc(food) + "cc");
  grad.addColorStop(1,     fc(food) + "66");
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.shadowBlur  = 0;
  ctx.strokeStyle = marked ? "#ffffff" : "rgba(255,255,255,0.5)";
  ctx.lineWidth   = marked ? 2.5 : 1.2;
  ctx.stroke();

  // glint
  ctx.beginPath();
  ctx.arc(x - r * 0.28, y - r * 0.3, r * 0.22, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.font = `${Math.max(10, Math.floor(r * 1.05))}px serif`;
  ctx.textAlign    = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(fe(food), x, y + 1);
  ctx.restore();
}

function drawAimDots(ctx, sx, sy, angle, W, R) {
  let ax = sx, ay = sy;
  let vx = Math.cos(angle), vy = Math.sin(angle);
  for (let i = 0; i < 44; i++) {
    ax += vx * R * 0.72; ay += vy * R * 0.72;
    if (ax < R)     { ax = R;     vx = -vx; }
    if (ax > W - R) { ax = W - R; vx = -vx; }
    if (ay < R * 0.5) break;
    if (i % 2 === 0) {
      ctx.beginPath();
      ctx.arc(ax, ay, clamp(2.8 - i * 0.06, 0.5, 2.8), 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,220,60,${Math.max(0, 0.9 - i * 0.022)})`;
      ctx.fill();
    }
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AvuruduBubbleShooter() {
  const canvasRef = useRef(null);
  const G         = useRef(null);   // mutable game state
  const rafRef    = useRef(null);
  const tickRef   = useRef(0);

  const [screen,  setScreen]  = useState("start");
  const [score,   setScore]   = useState(0);
  const [lives,   setLives]   = useState(3);
  const [level,   setLevel]   = useState(1);
  const [curFood, setCurFood] = useState(0);
  const [nxtFood, setNxtFood] = useState(1);
  const [popups,  setPopups]  = useState([]);

  // ── Popup helper ───────────────────────────────────────────────────────────
  const addPopup = useCallback((text, x, y) => {
    const id = Date.now() + Math.random();
    setPopups(ps => [...ps, { id, text, x, y }]);
    setTimeout(() => setPopups(ps => ps.filter(p => p.id !== id)), 1300);
  }, []);

  // ── Resize canvas to fullscreen ────────────────────────────────────────────
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const d = getDims();
    canvas.width  = d.W;
    canvas.height = d.H;
    if (G.current) {
      const old = G.current.dims;
      G.current.dims = d;
      G.current.shooter.x = d.W / 2;
      G.current.shooter.y = d.H - d.R * 2.5;
      G.current.bubbles = G.current.bubbles.map(b => {
        const xOff = b.row % 2 === 0 ? 0 : d.R;
        return { ...b,
          x: xOff + d.R + b.col * d.R * 2,
          y: d.R * 0.5 + b.row * d.R * 1.72,
          r: d.R - 2,
        };
      });
    }
  }, []);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [resizeCanvas]);

  // ── Refresh shooter bubble ─────────────────────────────────────────────────
  function refreshShooter(s) {
    s.shooter.food = s.nextFood;
    s.nextFood = rnd(Math.min(3 + Math.floor(s.level / 2), 6));
    setCurFood(s.shooter.food);
    setNxtFood(s.nextFood);
  }

  // ── Place projectile into grid ─────────────────────────────────────────────
  const placeProjectile = useCallback((s) => {
    const { R, COLS, W, H } = s.dims;
    const p = s.projectile;
    s.projectile = null;

    // find nearest empty grid cell
    let bestRow = -1, bestCol = 0, bestDist = Infinity;
    for (let row = 0; row < 18; row++) {
      const cols = row % 2 === 0 ? COLS : COLS - 1;
      const xOff = row % 2 === 0 ? 0 : R;
      for (let col = 0; col < cols; col++) {
        if (getBubble(s.bubbles, col, row)) continue;
        const bx = xOff + R + col * R * 2;
        const by = R * 0.5 + row * R * 1.72;
        const d  = Math.hypot(p.x - bx, p.y - by);
        if (d < bestDist && d < R * 2.7) { bestDist = d; bestRow = row; bestCol = col; }
      }
    }
    if (bestRow < 0) {
      bestRow = 0;
      bestCol = clamp(Math.round((p.x - R) / (R * 2)), 0, COLS - 1);
    }

    const nb = makeBubble(bestCol, bestRow, p.food, R);
    nb.scale  = 0.2;
    s.bubbles.push(nb);

    // ── Match detection ────────────────────────────────────────────────
    const matches = matchGroup(nb, s.bubbles);
    const cx = matches.reduce((a, b) => a + b.x, 0) / matches.length;
    const cy = matches.reduce((a, b) => a + b.y, 0) / matches.length;

    if (matches.length >= 3) {
      // Phase 1 — MARK: highlight matched bubbles with glowing ring
      matches.forEach(b => { b.state = "marked"; b.markT = 0; });

      // Phase 2 — BLAST: after short delay, pop them
      setTimeout(() => {
        matches.forEach(b => { b.state = "popping"; b.popT = 0; });

        // particle burst
        matches.forEach(b => {
          for (let i = 0; i < 10; i++) {
            const a = Math.random() * Math.PI * 2;
            const sp = 2.5 + Math.random() * 4;
            s.particles.push({
              x: b.x, y: b.y,
              vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
              life: 0.9 + Math.random() * 0.3,
              emoji: fe(b.food),
              size: 12 + Math.random() * 10,
            });
          }
        });

        const gained = matches.length * 10 * s.level;
        s.score += gained;
        setScore(s.score);
        addPopup(`+${gained} ✨`, cx, cy);

        // Phase 3 — floating bubble drop
        setTimeout(() => {
          const floating = floatingSet(s.bubbles);
          if (floating.length > 0) {
            floating.forEach(b => { b.state = "floating-fall"; b.vy = -1; b.alpha = 1; });
            const bonus = floating.length * 15 * s.level;
            s.score += bonus;
            setScore(s.score);
            if (bonus > 0) addPopup(`💥 +${bonus}`, cx, cy - 50);
          }

          // Phase 4 — check win / continue
          setTimeout(() => {
            const alive = s.bubbles.filter(b => b.state === "idle");
            if (alive.length === 0) {
              s.level++;
              setLevel(s.level);
              addPopup("🎊 Level Up!", W / 2, H / 2 - 40);
              setTimeout(() => {
                s.bubbles = buildGrid(s.level, s.dims.R, s.dims.COLS);
              }, 900);
            }
            s.canShoot = true;
            refreshShooter(s);
          }, 520);
        }, 460);
      }, 380);

    } else {
      // no match — danger check
      const sy = s.shooter.y;
      const danger = s.bubbles.filter(b => b.state === "idle" && b.y + b.r > sy - R * 3.2);
      if (danger.length > 0) {
        s.lives--;
        setLives(s.lives);
        if (s.lives <= 0) {
          s.active = false;
          setTimeout(() => setScreen("gameover"), 400);
          return;
        }
      }
      s.canShoot = true;
      refreshShooter(s);
    }
  }, [addPopup]);

  // ── Render loop ────────────────────────────────────────────────────────────
  const renderFrame = useCallback(() => {
    const s = G.current;
    if (!s || !s.active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { W, H, R } = s.dims;
    tickRef.current++;
    const tick = tickRef.current;

    // Background
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0,   "#0d0525");
    bg.addColorStop(0.55, "#2a0d55");
    bg.addColorStop(1,    "#4a1880");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Stars
    for (let i = 0; i < 50; i++) {
      const sx2 = ((i * 137 + 23) % W);
      const sy2 = ((i * 97  + 11) % (H * 0.72));
      const ss  = 0.6 + (i % 4) * 0.35;
      ctx.globalAlpha = 0.2 + 0.3 * Math.sin(tick * 0.018 + i * 0.7);
      ctx.beginPath();
      ctx.arc(sx2, sy2, ss, 0, Math.PI * 2);
      ctx.fillStyle = i % 5 === 0 ? "#ffd700" : "#ffffff";
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Aim dots
    if (s.canShoot && !s.projectile) {
      drawAimDots(ctx, s.shooter.x, s.shooter.y, s.shooter.angle, W, R);
    }

    // ── Grid bubbles ───────────────────────────────────────────────────
    const remove = new Set();
    s.bubbles.forEach(b => {
      if (b.state === "popping") {
        b.popT += 0.065;
        drawBubble(ctx, b.food, b.x, b.y, b.r * (1 + b.popT * 0.9), Math.max(0, 1 - b.popT * 1.3));
        if (b.popT >= 1) remove.add(b);

      } else if (b.state === "floating-fall") {
        b.vy = (b.vy || 0) + 0.55;
        b.y  += b.vy;
        b.alpha = Math.max(0, (b.alpha ?? 1) - 0.025);
        drawBubble(ctx, b.food, b.x, b.y, b.r, b.alpha);
        if (b.y > H + R * 2 || b.alpha <= 0) remove.add(b);

      } else if (b.state === "marked") {
        b.markT = (b.markT || 0) + 1;
        const pulse = 0.5 + 0.5 * Math.sin(b.markT * 0.28);

        // outer glow ring
        ctx.save();
        ctx.globalAlpha = 0.55 + 0.45 * pulse;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r + 5 + pulse * 5, 0, Math.PI * 2);
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth   = 2.5;
        ctx.shadowColor = fg(b.food);
        ctx.shadowBlur  = 14;
        ctx.stroke();
        ctx.restore();

        // inner second ring
        ctx.save();
        ctx.globalAlpha = 0.35 + 0.3 * pulse;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r + 10 + pulse * 8, 0, Math.PI * 2);
        ctx.strokeStyle = fg(b.food);
        ctx.lineWidth   = 1.5;
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();

        drawBubble(ctx, b.food, b.x, b.y, b.r, 1, pulse, true);

      } else {
        // idle
        if (b.scale < 1) b.scale = Math.min(1, b.scale + 0.1);
        drawBubble(ctx, b.food, b.x, b.y, b.r * b.scale, 1, 0, false);
      }
    });
    s.bubbles = s.bubbles.filter(b => !remove.has(b));

    // Particles
    s.particles.forEach(p => {
      p.x += p.vx; p.y += p.vy; p.vy += 0.2; p.life -= 0.028;
      if (p.life <= 0) return;
      ctx.save();
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.font = `${p.size}px serif`;
      ctx.textAlign    = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(p.emoji, p.x, p.y);
      ctx.restore();
    });
    s.particles = s.particles.filter(p => p.life > 0);

    // ── Projectile ────────────────────────────────────────────────────
    if (s.projectile) {
      const p = s.projectile;
      p.x += p.vx; p.y += p.vy;
      if (p.x - R < 0)     { p.x = R;     p.vx = -p.vx; }
      if (p.x + R > W)     { p.x = W - R; p.vx = -p.vx; }

      // trail
      for (let t = 1; t <= 5; t++) {
        drawBubble(ctx, p.food,
          p.x - p.vx * t * 0.55,
          p.y - p.vy * t * 0.55,
          (R - 2) * (0.82 - t * 0.14),
          Math.max(0, 0.22 - t * 0.04)
        );
      }
      drawBubble(ctx, p.food, p.x, p.y, R - 2, 1);

      const hitTop = p.y < R * 1.1;
      const hitBub = !hitTop && s.bubbles.find(
        b => b.state === "idle" && Math.hypot(p.x - b.x, p.y - b.y) < R * 1.78
      );
      if (hitTop || hitBub) { placeProjectile(s); }
      else if (p.y > H + R) { s.projectile = null; s.canShoot = true; refreshShooter(s); }
    }

    // ── Shooter ────────────────────────────────────────────────────────
    const sx = s.shooter.x, sy = s.shooter.y;

    // platform glow
    const rg = ctx.createRadialGradient(sx, sy, R * 0.4, sx, sy, R * 2.2);
    rg.addColorStop(0,   "rgba(255,180,0,0.0)");
    rg.addColorStop(0.6, "rgba(255,180,0,0.12)");
    rg.addColorStop(1,   "rgba(255,180,0,0.32)");
    ctx.beginPath();
    ctx.arc(sx, sy, R * 2.2, 0, Math.PI * 2);
    ctx.fillStyle = rg;
    ctx.fill();

    // base arc
    ctx.save();
    ctx.beginPath();
    ctx.arc(sx, sy + R * 0.55, R * 1.9, Math.PI * 0.18, Math.PI * 0.82);
    ctx.strokeStyle = "rgba(255,200,0,0.5)";
    ctx.lineWidth   = 3;
    ctx.stroke();
    ctx.restore();

    if (s.canShoot) drawBubble(ctx, s.shooter.food, sx, sy, R, 1);

    // aim arrow
    ctx.save();
    const arrowStart = R + 3;
    const arrowEnd   = R + 22;
    ctx.beginPath();
    ctx.moveTo(sx + Math.cos(s.shooter.angle) * arrowStart,
               sy + Math.sin(s.shooter.angle) * arrowStart);
    ctx.lineTo(sx + Math.cos(s.shooter.angle) * arrowEnd,
               sy + Math.sin(s.shooter.angle) * arrowEnd);
    ctx.strokeStyle = "rgba(255,215,0,0.88)";
    ctx.lineWidth   = 3.5;
    ctx.lineCap     = "round";
    ctx.shadowColor = "#ff9900";
    ctx.shadowBlur  = 8;
    ctx.stroke();
    ctx.restore();

    // danger line
    ctx.save();
    ctx.setLineDash([10, 8]);
    ctx.strokeStyle = "rgba(255,60,60,0.32)";
    ctx.lineWidth   = 1.5;
    ctx.beginPath();
    ctx.moveTo(0,  sy - R * 3.2);
    ctx.lineTo(W,  sy - R * 3.2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // footer watermark
    ctx.save();
    ctx.font      = `bold ${Math.max(9, R * 0.52)}px sans-serif`;
    ctx.fillStyle = "rgba(255,215,0,0.1)";
    ctx.textAlign = "center";
    ctx.fillText("🎊 සිංහල හා දමිළ අලුත් අවුරුදු ශුභ වේවා! 🎊", W / 2, H - 5);
    ctx.restore();

    rafRef.current = requestAnimationFrame(renderFrame);
  }, [placeProjectile]);

  // ── Shoot ──────────────────────────────────────────────────────────────────
  const shoot = useCallback(() => {
    const s = G.current;
    if (!s || !s.canShoot || !s.active || s.projectile) return;
    s.canShoot   = false;
    s.projectile = {
      x: s.shooter.x, y: s.shooter.y,
      food: s.shooter.food,
      vx: Math.cos(s.shooter.angle) * 10,
      vy: Math.sin(s.shooter.angle) * 10,
    };
    // launch sparkle
    for (let i = 0; i < 6; i++) {
      const a = s.shooter.angle + (Math.random() - 0.5) * 0.5;
      s.particles.push({
        x: s.shooter.x, y: s.shooter.y,
        vx: Math.cos(a) * (3 + Math.random() * 3),
        vy: Math.sin(a) * (3 + Math.random() * 3),
        life: 0.6 + Math.random() * 0.3,
        emoji: fe(s.shooter.food),
        size: 9 + Math.random() * 7,
      });
    }
  }, []);

  // ── Aim ────────────────────────────────────────────────────────────────────
  const aim = useCallback((clientX, clientY) => {
    const s = G.current;
    if (!s || !s.active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const { W, H } = s.dims;
    const cx = (clientX - rect.left) * W / rect.width;
    const cy = (clientY - rect.top)  * H / rect.height;
    let a = Math.atan2(cy - s.shooter.y, cx - s.shooter.x);
    a = clamp(a, -Math.PI + 0.12, -0.12);
    s.shooter.angle = a;
  }, []);

  // ── Start / Restart ────────────────────────────────────────────────────────
  const startGame = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    const d  = getDims();
    const canvas = canvasRef.current;
    if (canvas) { canvas.width = d.W; canvas.height = d.H; }
    const lv = 1;
    const sf = rnd(3);
    const nf = rnd(3);

    G.current = {
      active: true, dims: d,
      bubbles: buildGrid(lv, d.R, d.COLS),
      shooter: { x: d.W / 2, y: d.H - d.R * 2.5, angle: -Math.PI / 2, food: sf },
      nextFood: nf,
      projectile: null, particles: [],
      score: 0, lives: 3, level: lv,
      canShoot: true,
    };

    setScore(0); setLives(3); setLevel(1);
    setCurFood(sf); setNxtFood(nf);
    setPopups([]);
    setScreen("play");
    tickRef.current = 0;
    rafRef.current  = requestAnimationFrame(renderFrame);
  }, [renderFrame]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // ── Input handlers ────────────────────────────────────────────────────────
  const onMouseMove  = e => aim(e.clientX, e.clientY);
  const onClick      = e => { aim(e.clientX, e.clientY); shoot(); };
  const onTouchStart = e => { e.preventDefault(); const t = e.touches[0]; aim(t.clientX, t.clientY); shoot(); };
  const onTouchMove  = e => { e.preventDefault(); const t = e.touches[0]; aim(t.clientX, t.clientY); };

  // ─── UI styles ───────────────────────────────────────────────────────────
  const livesArr = Array.from({ length: 3 }, (_, i) => i < lives ? "❤️" : "🖤");

  const css = {
    root: {
      position: "fixed", inset: 0, overflow: "hidden",
      fontFamily: "'Segoe UI', Arial, sans-serif",
      userSelect: "none", touchAction: "none",
    },
    canvas: {
      display: "block", width: "100%", height: "100%",
      cursor: "crosshair", touchAction: "none",
    },
    hud: {
      position: "absolute", top: 0, left: 0, right: 0, zIndex: 20,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "6px 14px",
      background: "linear-gradient(135deg,#ff6b00cc,#ffd700bb,#ff6b00cc)",
      backdropFilter: "blur(8px)",
      borderBottom: "2px solid rgba(255,190,0,0.55)",
      boxShadow: "0 3px 20px rgba(255,120,0,0.4)",
    },
    hudTitle: { fontSize: "clamp(9px,2.4vw,13px)", fontWeight: 700, color: "#1a0500", lineHeight: 1.3 },
    hudSub:   { fontSize: "clamp(8px,1.8vw,10px)", color: "#4a1800" },
    scoreBox: {
      background: "rgba(0,0,0,0.32)", borderRadius: 8, padding: "3px 12px",
      color: "#ffd700", fontSize: "clamp(15px,4vw,22px)", fontWeight: 700,
      textShadow: "0 0 12px #ffaa00", border: "1px solid rgba(255,215,0,0.4)",
    },
    bottom: {
      position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 20,
      display: "flex", alignItems: "center", justifyContent: "space-around",
      padding: "8px 20px",
      background: "linear-gradient(0deg,rgba(10,2,36,0.92),rgba(30,10,60,0.75))",
      backdropFilter: "blur(8px)",
      borderTop: "2px solid rgba(255,180,0,0.3)",
    },
    bubbleSlot: {
      display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
      color: "#ffd700", fontSize: "clamp(9px,2.3vw,12px)", fontWeight: 600,
    },
    bubbleCircle: (accent) => ({
      width: "clamp(32px,8.5vw,46px)", height: "clamp(32px,8.5vw,46px)",
      borderRadius: "50%", border: `2px solid ${accent}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: "clamp(16px,5vw,28px)",
      background: "rgba(255,180,0,0.1)",
      boxShadow: `0 0 10px ${accent}55`,
    }),
    hint: {
      color: "rgba(255,215,0,0.62)", fontSize: "clamp(8px,2.1vw,11px)",
      textAlign: "center", lineHeight: 1.55, maxWidth: 220,
    },
    overlay: {
      position: "absolute", inset: 0, zIndex: 50,
      background: "rgba(8,0,28,0.93)",
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", gap: "clamp(10px,2.5vh,18px)",
      padding: "24px 22px", overflowY: "auto",
    },
    bigEmoji: { fontSize: "clamp(40px,12vw,60px)" },
    title: {
      fontSize: "clamp(20px,6vw,32px)", fontWeight: 700,
      color: "#ffd700", textAlign: "center",
      textShadow: "0 0 28px #ff9900, 0 0 60px #ff660055",
      lineHeight: 1.35,
    },
    body: {
      color: "#ffcc88", fontSize: "clamp(11px,3vw,14px)",
      textAlign: "center", lineHeight: 1.75, maxWidth: 360,
    },
    btn: {
      background: "linear-gradient(135deg,#ff6b00,#ffcc00)",
      color: "#1a0500", fontWeight: 700, fontSize: "clamp(13px,3.5vw,17px)",
      padding: "13px 38px", borderRadius: 30, border: "none", cursor: "pointer",
      boxShadow: "0 5px 26px rgba(255,140,0,0.65)", letterSpacing: "0.02em",
    },
    legend: {
      display: "flex", flexWrap: "wrap", gap: "8px 12px",
      justifyContent: "center", maxWidth: 360,
    },
    litem: {
      display: "flex", alignItems: "center", gap: 6,
      fontSize: "clamp(10px,2.8vw,13px)", color: "#ffcc88",
      background: "rgba(255,180,0,0.1)", borderRadius: 20,
      padding: "4px 12px", border: "1px solid rgba(255,180,0,0.22)",
    },
    ruleBox: {
      display: "flex", flexDirection: "column", gap: 6,
      background: "rgba(255,180,0,0.07)", borderRadius: 12,
      padding: "10px 18px", border: "1px solid rgba(255,180,0,0.2)",
      maxWidth: 340,
    },
    ruleRow: {
      display: "flex", alignItems: "center", gap: 8,
      fontSize: "clamp(10px,2.8vw,13px)", color: "#ffdd99",
    },
  };

  return (
    <div style={css.root}>
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        style={css.canvas}
        onMouseMove={onMouseMove}
        onClick={onClick}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
      />

      {/* HUD */}
      {screen === "play" && (
        <>
          <div style={css.hud}>
            <div>
              <div style={css.hudTitle}>🎊 සිංහල හා දමිළ අලුත් අවුරුදු</div>
              <div style={css.hudSub}>Kokis Shooter · Level {level}</div>
            </div>
            <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
              {livesArr.map((h, i) => <span key={i} style={{ fontSize: "clamp(12px,3vw,16px)" }}>{h}</span>)}
            </div>
            <div style={css.scoreBox}>{score}</div>
          </div>

          <div style={css.bottom}>
            <div style={css.bubbleSlot}>
              <span>ඊළඟ</span>
              <div style={css.bubbleCircle("#ffd700")}>{fe(nxtFood)}</div>
            </div>
            <div style={css.hint}>
              🎯 ක්ලික් හෝ ස්පර්ශ කරන්න<br />
              3+ ගැලපෙනා → <span style={{ color: "#ff9900" }}>mark</span> →{" "}
              <span style={{ color: "#ff4444" }}>💥 blast!</span>
            </div>
            <div style={css.bubbleSlot}>
              <span>දැන්</span>
              <div style={css.bubbleCircle("#ff9900")}>{fe(curFood)}</div>
            </div>
          </div>
        </>
      )}

      {/* Score popups */}
      <style>{`
        @keyframes floatUp {
          0%   { opacity:1; transform:translate(-50%,-50%) scale(0.9); }
          40%  { opacity:1; transform:translate(-50%,-110%) scale(1.25); }
          100% { opacity:0; transform:translate(-50%,-200%) scale(0.85); }
        }
      `}</style>
      {popups.map(p => (
        <div key={p.id} style={{
          position: "absolute", zIndex: 40, pointerEvents: "none",
          left: p.x, top: p.y, transform: "translate(-50%,-50%)",
          color: "#ffd700", fontWeight: 700,
          fontSize: "clamp(16px,4.5vw,24px)",
          textShadow: "0 0 14px #ff6600, 0 2px 5px #000000cc",
          animation: "floatUp 1.3s ease-out forwards",
          whiteSpace: "nowrap",
        }}>{p.text}</div>
      ))}

      {/* START */}
      {screen === "start" && (
        <div style={css.overlay}>
          <div style={css.bigEmoji}>🎊</div>
          <div style={css.title}>සිංහල හා දමිළ<br />අලුත් අවුරුදු!</div>
          <div style={css.body}>
            ආහාර බුබුළු ඉලක්ක කර වෙඩි දමන්න!
          </div>

          <div style={css.ruleBox}>
            <div style={css.ruleRow}><span>🎯</span><span>ක්ලික් / ස්පර්ශ → ගිනි දමන්න</span></div>
            <div style={css.ruleRow}><span>✨</span><span>3+ එකම ආහාර ගැලපෙනවිට <strong style={{color:"#ffd700"}}>mark</strong> වෙනවා</span></div>
            <div style={css.ruleRow}><span>💥</span><span>ටික වේලාවකට පස්සෙ <strong style={{color:"#ff4444"}}>blast!</strong></span></div>
            <div style={css.ruleRow}><span>🌊</span><span>රඳා පැවති බුබුළු ද වැටෙනවා — bonus!</span></div>
          </div>

          <div style={css.legend}>
            {FOODS.map((f, i) => (
              <div key={i} style={css.litem}><span>{f}</span><span>{FNAMES[i]}</span></div>
            ))}
          </div>
          <button style={css.btn} onClick={startGame}>🎮 ක්‍රීඩාව ආරම්භ කරන්න!</button>
        </div>
      )}

      {/* GAME OVER */}
      {screen === "gameover" && (
        <div style={css.overlay}>
          <div style={css.bigEmoji}>😢</div>
          <div style={css.title}>ක්‍රීඩාව අවසානයි!</div>
          <div style={css.body}>
            <span style={{ fontSize: "clamp(26px,7vw,38px)", color: "#ffd700", display: "block" }}>{score}</span>
            ලකුණු · මට්ටම {level} දක්වා
          </div>
          <button style={css.btn} onClick={startGame}>🔄 නැවත ක්‍රීඩා කරන්න</button>
        </div>
      )}
    </div>
  );
}